import { useState, useEffect } from 'react'
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip
} from '@material-ui/core'
import { Alert, Skeleton } from '@material-ui/lab'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { orange } from '@material-ui/core/colors'

import { obtenerMovimientoCuentaCliente } from '../../../ducks/clientesDuck'
import { formatDinero } from "./../../../utils/formatDinero"
import { FormatoFechaIso } from '../../../utils/FormatFecha'
import { catRoles } from '../../../constant/catRoles'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow: theme.shadows[3],
  },
  container: {
    maxHeight: 600,
  },
  headerCell: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 'bold',
    textAlign: 'center',
  },
  cellContent: {
    textAlign: "center",
    fontSize: "0.95rem"
  },
  icon: {
    fontSize: "1.4rem"
  },
  corteCajaWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column"
  },
  tipoPagoBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#fff',
    textTransform: 'capitalize'
  },
  tipoOpenPay: {
    backgroundColor: '#1976d2'
  },
  tipoManual: {
    backgroundColor: '#f57c00'
  },
  tipoBancario: {
    backgroundColor: '#388e3c'
  }
}))

const columns = [
  { id: "claveRastreo", label: "Clave de rastreo", minWidth: 150 },
  { id: "monto", label: "Monto", minWidth: 100 },
  { id: "tipoCambio", label: "T. C.", minWidth: 80 },
  { id: "conversion", label: "Conversión", minWidth: 100 },
  { id: "fecha", label: "Fecha", minWidth: 150 },
  { id: "tipoPago", label: "Tipo de pago", minWidth: 100 },
  { id: "corteCaja", label: "Corte de caja", minWidth: 120 },
]

export default function TPagos() {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { referencia } = useSelector(store => store.cliente)
  const { profile } = useSelector(store => store.logeo)
  const rows = useSelector(store => store.cliente.movimientos)

  useEffect(() => {
    if (referencia) {
      dispatch(obtenerMovimientoCuentaCliente({ referencia }))
    }
  }, [dispatch, referencia])

  const getRowStyle = (row) => {
    if (row.EsCorteCaja) {
      if (row.CorteCaja?.TipoCambio > 0) {
        return { backgroundColor: "#e8f5e9" } // Verde claro
      } else {
        return { backgroundColor: "#fffde7" } // Amarillo claro
      }
    }
    return {}
  }

  const renderTipoPago = (tipo) => {
    const baseClass = classes.tipoPagoBadge
    if (!tipo) return <span className={baseClass}>Desconocido</span>
    const tipoLower = tipo.toLowerCase()    
    const tipoClass =
      tipoLower === 'openpay' ? classes.tipoOpenPay :
      tipoLower === 'pago manual' ? classes.tipoManual :
      tipoLower === 'deposito bancario' ? classes.tipoBancario : ''
    return (
      <span className={`${baseClass} ${tipoClass}`}>
        {tipo}
      </span>
    )
  }

  if (!referencia)
    return (
      <div className="TarjetaClienta-root">
        <Alert className="alerta" severity="info">Seleccione un cliente</Alert>
        <Skeleton variant="rect" height={250} />
      </div>
    )

  if (rows.length === 0)
    return (
      <div className="TarjetaClienta-root">
        <Alert className="alerta" severity="info">No hay movimientos para mostrar</Alert>
      </div>
    )

  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  className={classes.headerCell}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id_movCuenta} style={getRowStyle(row)}>
                <TableCell className={classes.cellContent}>
                  <div>{row.Referencia}</div>
                  <div style={{ fontSize: "0.8rem", color: "#666" }}>{row.ClaveRastreo}</div>
                </TableCell>
                <TableCell className={classes.cellContent}>{formatDinero(row.monto)}</TableCell>
                <TableCell className={classes.cellContent}>{formatDinero(row.tipoCambio)}</TableCell>
                <TableCell className={classes.cellContent}>{formatDinero(row.monto / row.tipoCambio)}</TableCell>
                <TableCell className={classes.cellContent}>
                  {FormatoFechaIso({ date: row.Fecha, formato: "dd MMM yyyy" })}
                </TableCell>
                <TableCell className={classes.cellContent}>
                  {renderTipoPago(row.TipoPago)}
                </TableCell>
                <TableCell className={classes.cellContent}>
                  <div className={classes.corteCajaWrapper}>
                    {row?.EsCorteCaja ? (
                      row?.CorteCaja?.TipoCambio > 0 ? (
                        <>
                          <Tooltip title="Aplicado" arrow>
                            <CheckCircleIcon className={classes.icon} color="secondary" />
                          </Tooltip>
                          {profile?.Rol?.Id === catRoles.ADMINISTRADOR &&
                            <div style={{ fontSize: "0.8rem", color: "#333" }}>
                              {formatDinero(row.CorteCaja?.TipoCambio)}
                            </div>
                          }
                        </>
                      ) : (
                        <Tooltip title="No aplicado" arrow>
                          <ErrorIcon className={classes.icon} style={{ color: orange[400] }} />
                        </Tooltip>
                      )
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: "#888" }}>No aplica</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
